import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { useSubscription, useSubscriptionTiers } from '../hooks';
import { SubscriptionCard } from '../components/SubscriptionCard';
import { TierPricingCard } from '../components/TierPricingCard';

export function SubscriptionPage() {
  const {
    subscription,
    loading: subLoading,
    error: subError,
    refresh: refreshSub,
  } = useSubscription();
  const { tiers, loading: tiersLoading, error: tiersError } = useSubscriptionTiers();
  const [purchasing, setPurchasing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const error = subError || tiersError || actionError;

  const handlePurchase = async (tier: string) => {
    setPurchasing(true);
    setActionError(null);
    try {
      await api.purchaseGameRewardsSubscription(tier);
      refreshSub();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to purchase subscription');
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_game_rewards"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Link>
        <h1 className="text-2xl font-semibold">Subscription</h1>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div>
        <h2 className="text-lg font-medium mb-3">Current Subscription</h2>
        {subLoading ? (
          <Skeleton className="h-40 w-full max-w-sm rounded-lg" />
        ) : (
          <div className="max-w-sm">
            <SubscriptionCard subscription={subscription} />
          </div>
        )}
      </div>

      <div className="rounded-md border border-muted bg-muted/30 p-3">
        <p className="text-sm">
          Want to skip Lit Gems?{' '}
          <Link
            to="/a/arche_game_rewards/store"
            className="text-primary hover:underline font-medium"
          >
            Buy a Lit Pass directly with credits in the Store
          </Link>
        </p>
      </div>

      <div>
        <h2 className="text-lg font-medium mb-3">Available Tiers</h2>
        {tiersLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-48 rounded-lg" />
            ))}
          </div>
        ) : tiers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tiers available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.map((tier) => (
              <TierPricingCard
                key={tier.tierId}
                tier={tier}
                currentTier={subscription?.tier ?? null}
                onPurchase={handlePurchase}
                purchasing={purchasing}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
